/**
 * PATH: packages/email-hub/src/feature/components/rich-text/quoteBlock.ts
 * FILE_ID: EMAILHUB-RICHTEXT-QUOTEBLOCK-001
 * PURPOSE: Pure I→O helpers that let a quoted ORIGINAL email containing
 *          layout tables survive the ProseMirror editor untouched, carried
 *          inside ONE opaque `<div data-qte-quote="…">` (the signature-block
 *          trick applied to the reply/forward quote).
 * WHY:     BUG-2026-07-07-EMAIL-QUOTE-TABLE-GRID: replying to an Outlook/
 *          newsletter email pushed its borderless spacer tables through the
 *          prosemirror-tables schema, which normalised them into a
 *          rectangular grid; the editor stylesheet then painted EVERY cell
 *          with a 1px border + min-width, rendering a glitchy block of empty
 *          boxes in the compose body. Table-free quotes keep the existing
 *          inline, editable behaviour (zero regression for plain replies).
 * FLOW:
 *   quoted original html ──wrapQuoteLayout──▶ <div data-qte-quote="enc">
 *   editor round-trips the opaque div verbatim (no table parse)
 *   send ──expandQuoteBlocks (emailHtml.toEmailHtml)──▶ faithful original
 * RELATED:
 *   - signatureBlock.ts: encode/decode + expandBlocksByAttr (shared engine)
 *   - quoteBlockNode.tsx: the TipTap atom node + NodeView
 *   - useComposePage.ts: reply/forward prefill + buildQuoteHtml (callers)
 */

import {
  encodeSignatureBlock,
  expandBlocksByAttr,
} from './signatureBlock'

/** The marker attribute that flags an opaque quoted-original block. */
export const QUOTE_BLOCK_ATTR = 'data-qte-quote'

/**
 * Should this quoted original be carried as an opaque block? TRUE only when
 * it contains a table - the one construct prosemirror-tables mangles into
 * the empty-cell grid. Everything else (plain text, divs, images, lists)
 * round-trips fine inline and stays editable/trimmable. Pure: html → boolean.
 */
export function shouldPreserveQuoteLayout(html: string | null | undefined): boolean {
  return /<table[\s>]/i.test(html || '')
}

/** Wrap quoted-original HTML for SEEDING into the editor: opaque when it
 *  carries a layout table, otherwise unchanged. Blank in → blank out.
 *  Pure: html → html. */
export function wrapQuoteLayout(html: string | null | undefined): string {
  const s = html || ''
  if (!s.trim()) return ''
  if (!shouldPreserveQuoteLayout(s)) return s
  return `<div ${QUOTE_BLOCK_ATTR}="${encodeSignatureBlock(s)}"></div>`
}

/** Replace every opaque quote block with its decoded raw HTML (send /
 *  preview). Pure: html → html. */
export function expandQuoteBlocks(html: string | null | undefined): string {
  return expandBlocksByAttr(html, QUOTE_BLOCK_ATTR)
}

/** True when the HTML contains at least one opaque quote block. */
export function hasQuoteBlock(html: string | null | undefined): boolean {
  return (html || '').includes(QUOTE_BLOCK_ATTR)
}
